import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, ClipboardList, Palette, Sliders, Disc3, Megaphone, Music, Layers3, Film, Zap, Calendar, ListMusic, Clock3, Workflow, Plug, SkipBack, Pause, SkipForward, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import VoiceTrackPanel from "@/components/production/VoiceTrackPanel";

const NavItem = ({ icon: Icon, label, to, onClick, active, testid }) => {
  const cls = `flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition cursor-pointer ${active ? "bg-brand-amber/10 text-brand-amber font-bold" : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"}`;
  return to ? (
    <Link to={to} className={cls} data-testid={testid}><Icon className="h-3.5 w-3.5" /> {label}</Link>
  ) : (
    <button onClick={onClick} className={`${cls} w-full text-left`} data-testid={testid}><Icon className="h-3.5 w-3.5" /> {label}</button>
  );
};
const Group = ({ title, children }) => (
  <div className="space-y-0.5">
    <div className="px-2.5 pt-3 pb-1 font-mono text-[8px] uppercase tracking-[0.25em] text-ink-muted">{title}</div>
    {children}
  </div>
);

const KIND_META = {
  weather: ["Weather", "border-sky-400/40 bg-sky-400/10 text-sky-300"],
  song_fact: ["Song Fact", "border-brand-amber/40 bg-brand-amber/10 text-brand-amber"],
  music_history: ["Today in Music", "border-violet-400/40 bg-violet-400/10 text-violet-300"],
  bit: ["Talk Bit", "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"],
};

function ShowPrepPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [city, setCity] = useState("");
  const [gen, setGen] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState("onyx");
  const [breaking, setBreaking] = useState("");
  const [sched, setSched] = useState(null);

  useEffect(() => {
    api.get("/production/show-prep")
      .then((r) => { setItems(r.data.items || []); setCity(r.data.weather_city || ""); })
      .catch(() => setItems([]));
    api.get("/voice/intro-voices").then((r) => setVoices(r.data.voices || [])).catch(() => {});
    api.get("/production/show-prep/schedule").then((r) => setSched(r.data)).catch(() => {});
  }, []);

  const fmtHour = (h) => (h === 0 ? "12:00 AM" : h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`);

  const saveSched = async (patch) => {
    const prev = sched;
    const next = { ...sched, ...patch };
    setSched(next);
    try {
      const r = await api.put("/production/show-prep/schedule", { enabled: next.enabled, hour: next.hour });
      setSched(r.data);
      toast.success(r.data.enabled ? `Auto-prep on — every morning at ${fmtHour(r.data.hour)}` : "Auto-prep off");
    } catch (e) {
      setSched(prev);
      toast.error(e?.response?.data?.detail || "Couldn't save the schedule");
    }
  };

  const generate = async () => {
    setGen(true);
    try {
      const r = await api.post("/production/show-prep/generate", { weather_city: city.trim() });
      setItems(r.data.items || []);
      toast.success("Today's show prep is ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prep generation failed — try again");
    }
    setGen(false);
  };

  const makeBreak = async (prepId) => {
    setBreaking(prepId);
    try {
      const r = await api.post(`/production/show-prep/${prepId}/voice-break`, { voice });
      setItems((xs) => xs.map((x) => (x.prep_id === prepId ? { ...x, vt_id: r.data.vt_id } : x)));
      window.dispatchEvent(new Event("vt-library-refresh"));
      toast.success("Voice break produced — it's waiting in VoiceTrack Studio");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice break failed — try again");
    }
    setBreaking("");
  };

  return (
    <div id="showprep-panel" className="rounded-xl border border-line bg-bg-surface" data-testid="showprep-panel">
      <div className="flex items-center gap-2 border-b border-line px-4 py-2.5 text-sm font-bold">
        <ClipboardList className="h-4 w-4 text-brand-cyan" /> Show Prep &amp; Planner
        <span className="ml-auto rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-px font-mono text-[8px] uppercase tracking-wider text-emerald-300">Live · AI</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Weather city (optional)"
            data-testid="prep-city-input" maxLength={60}
            className="flex-1 min-w-[130px] rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-xs" />
          <button onClick={generate} disabled={gen} data-testid="prep-generate-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-xs font-bold text-black disabled:opacity-50">
            {gen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {gen ? "Writing prep…" : "Generate today's prep"}
          </button>
        </div>
        {sched && (
          <label className="flex flex-wrap items-center gap-2 rounded-lg border border-line/60 bg-bg-elevated/40 px-3 py-2 text-[11px] text-ink-secondary cursor-pointer" data-testid="prep-sched-row">
            <input type="checkbox" checked={!!sched.enabled} onChange={(e) => saveSched({ enabled: e.target.checked })} data-testid="prep-sched-toggle" />
            <span className="font-bold">Auto-generate every morning</span> at
            <select value={sched.hour} onChange={(e) => saveSched({ hour: Number(e.target.value) })} data-testid="prep-sched-hour"
              className="rounded-md border border-line bg-bg-elevated px-1.5 py-1 text-[10px]">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{fmtHour(h)}</option>)}
            </select>
            <span className="text-ink-muted">({sched.timezone}{sched.last_run_date ? ` · last auto-run ${sched.last_run_date}` : ""})</span>
          </label>
        )}
        {gen && <p className="text-[10px] text-ink-muted">AI is researching song facts, music history and your local weather — about 20-40 seconds.</p>}
        {items === null ? (
          <div className="py-6 text-center text-xs text-ink-muted">Loading…</div>
        ) : !items.length ? (
          <div className="rounded-lg border border-dashed border-line p-5 text-center" data-testid="prep-empty">
            <p className="text-xs font-bold text-ink-secondary">No prep for today yet</p>
            <p className="mt-1 text-[11px] text-ink-muted">One click gets you 2 song facts, a today-in-music-history moment and a talk bit — plus local weather if you set a city.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted">Break voice</span>
              <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="prep-voice-select"
                className="max-w-[180px] rounded-md border border-line bg-bg-elevated px-2 py-1 text-[10px]">
                {(voices.length ? voices : [{ id: "onyx", label: "Male — Deep Authority" }]).map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
            {items.map((it) => {
              const [kLabel, kCls] = KIND_META[it.kind] || KIND_META.bit;
              return (
                <div key={it.prep_id} className="rounded-lg border border-line bg-bg-elevated/50 p-3" data-testid={`prep-item-${it.prep_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`shrink-0 rounded-full border px-2 py-px font-mono text-[8px] uppercase tracking-wider ${kCls}`}>{kLabel}</span>
                    <div className="text-xs font-bold truncate">{it.title}</div>
                  </div>
                  <p className="text-[11px] text-ink-muted mt-1.5 leading-relaxed">{it.body}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {it.vt_id ? (
                      <button onClick={() => navigate("/dashboard/production/voicetrack")} data-testid={`prep-open-vt-${it.prep_id}`}
                        className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-[10px] font-bold text-emerald-300">
                        Break ready — open VoiceTrack Studio ↗
                      </button>
                    ) : (
                      <button onClick={() => makeBreak(it.prep_id)} disabled={!!breaking} data-testid={`prep-genbreak-${it.prep_id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1 text-[10px] font-bold text-black disabled:opacity-50">
                        {breaking === it.prep_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mic className="h-3 w-3" />}
                        {breaking === it.prep_id ? "Producing…" : "Generate Voice Break"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function AudioProcessorPanel({ presets, meters }) {
  const [active, setActive] = useState(0);
  return (
    <div className="rounded-xl border border-line bg-bg-surface" data-testid="processor-panel">
      <div className="flex items-center gap-2 border-b border-line px-4 py-2.5 text-sm font-bold">
        <Sliders className="h-4 w-4 text-violet-300" /> Audio Processor
      </div>
      <div className="p-4 space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {(presets || []).map((p, i) => (
            <button key={p} onClick={() => setActive(i)} data-testid={`proc-preset-${i}`}
              className={`rounded-full border px-3 py-1 text-[10px] font-bold transition ${i === active ? "border-brand-amber/60 bg-brand-amber/10 text-brand-amber" : "border-line text-ink-secondary"}`}>
              {p}
            </button>
          ))}
        </div>
        <div className="flex items-end justify-around" data-testid="proc-meters">
          {(meters || []).map((m) => (
            <div key={m.k} className="text-center">
              <div className="mx-auto h-16 w-2.5 rounded-full bg-bg-elevated overflow-hidden flex flex-col justify-end">
                <div className="w-full rounded-full" style={{ height: `${40 + (m.k.length * 9) % 45}%`, background: "linear-gradient(to top,#22c55e,#eab308,#ef4444)" }} />
              </div>
              <div className="mt-1.5 font-mono text-[8px] uppercase text-ink-muted">{m.k}</div>
              <div className="font-mono text-[9px] text-ink-secondary">{m.v}</div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-brand-cyan/80">⚡ Extends Production Suite's existing FX chain — AGC / Multiband / LUFS are the new nodes (Phase 5).</p>
      </div>
    </div>
  );
}

export default function ProductionHub() {
  const navigate = useNavigate();
  const [sample, setSample] = useState(null);
  const [providers, setProviders] = useState([]);
  const [onAir, setOnAir] = useState(0);

  useEffect(() => {
    api.get("/production/hub/sample").then((r) => setSample(r.data)).catch(() => {});
    api.get("/push-to-air/providers").then((r) => setProviders((r.data.providers || []).slice(0, 5))).catch(() => {});
    const id = setInterval(() => setOnAir((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const soon = (label, phase) => () => toast.info(`${label} arrives in ${phase} — the schema is already in place.`);
  const hhmmss = (s) => `${String(Math.floor(s / 3600) + 1).padStart(2, "0")}:${String(Math.floor((s / 60) % 60) + 24).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-3rem)]" data-testid="production-hub">
      <div className="flex flex-1 min-h-0">
        {/* Inner sidebar */}
        <aside className="w-52 shrink-0 border-r border-line bg-bg-surface/60 py-2 px-1.5 hidden md:block overflow-y-auto" data-testid="prod-sidebar">
          <Group title="Production Suite">
            <NavItem icon={Mic} label="VoiceTrack Studio" active onClick={() => navigate("/dashboard/production/voicetrack")} testid="prod-nav-voicetrack" />
            <NavItem icon={ClipboardList} label="Show Prep & Planner" onClick={() => document.getElementById("showprep-panel")?.scrollIntoView({ behavior: "smooth" })} testid="prod-nav-showprep" />
            <NavItem icon={Palette} label="Radio Imaging Studio" to="/dashboard/imaging" testid="prod-nav-imaging" />
            <NavItem icon={Sliders} label="Audio Processor" onClick={soon("Audio Processor gap-fill", "Phase 5")} testid="prod-nav-processor" />
            <NavItem icon={Disc3} label="Rip & Tag Studio" to="/dashboard/rip-tag-studio" testid="prod-nav-riptag" />
            <NavItem icon={Megaphone} label="Commercial Studio" to="/dashboard/commercials" testid="prod-nav-commercial" />
          </Group>
          <Group title="Libraries">
            <NavItem icon={Music} label="Music Library" onClick={soon("The unified audio_assets library", "a later phase")} testid="prod-nav-lib-music" />
            <NavItem icon={Layers3} label="Voice Tracks" onClick={() => navigate("/dashboard/production/voicetrack")} testid="prod-nav-lib-vt" />
            <NavItem icon={Palette} label="Imaging Library" to="/dashboard/imaging" testid="prod-nav-lib-imaging" />
            <NavItem icon={Film} label="Drop Library" onClick={soon("The Drops library (station-owned, rights-attested)", "Phase 4")} testid="prod-nav-lib-drops" />
            <NavItem icon={Zap} label="SFX Library" to="/dashboard/production-suite" testid="prod-nav-lib-sfx" />
          </Group>
          <Group title="Scheduler">
            <NavItem icon={Calendar} label="Calendar" onClick={soon("Scheduler", "Phase 6")} testid="prod-nav-calendar" />
            <NavItem icon={ListMusic} label="Playlist Builder" onClick={soon("Playlist Builder", "Phase 6")} testid="prod-nav-playlist" />
            <NavItem icon={Clock3} label="Clockwheels" onClick={soon("Clockwheels", "Phase 6")} testid="prod-nav-clockwheels" />
            <NavItem icon={Workflow} label="Automation Rules" onClick={soon("Automation Rules", "Phase 6")} testid="prod-nav-automation" />
          </Group>
          <Group title="Integrations">
            {providers.map((p) => (
              <NavItem key={p.id} icon={Plug} label={p.label} to="/dashboard/integrations" testid={`prod-nav-int-${p.id}`} />
            ))}
          </Group>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 overflow-y-auto p-4 space-y-4 pb-20">
          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-4 py-2.5 text-xs text-sky-200" data-testid="prod-phase-banner">
            <b>VoiceTrack Studio &amp; Show Prep are live:</b> record with real song beds and auto-ducking below, and generate AI show prep that becomes one-click voice breaks. The Audio Processor panel still shows sample data until Phase 5.
          </div>
          <div className="grid xl:grid-cols-[3fr_2fr] gap-4 items-start">
            <VoiceTrackPanel docked />
            <div className="space-y-4">
              <ShowPrepPanel items={sample?.show_prep} />
              <AudioProcessorPanel presets={sample?.processor_presets} meters={sample?.processor_meters} />
            </div>
          </div>
        </main>
      </div>

      {/* Transport footer */}
      <footer className="sticky bottom-0 border-t border-line bg-bg-surface/95 backdrop-blur px-4 py-2.5 flex items-center gap-4" data-testid="prod-transport">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-md shrink-0" style={{ background: "linear-gradient(135deg,#6366f1,#a855f7)" }} />
          <div className="min-w-0">
            <div className="text-xs font-bold truncate">{sample ? `${sample.previous_song.artist} — ${sample.previous_song.title}` : "—"}</div>
            <div className="text-[9px] text-ink-muted truncate">02:41 / 03:50 · Next: {sample ? `${sample.next_song.artist} — ${sample.next_song.title}` : "—"} <span className="rounded bg-white/10 px-1 font-mono text-[8px]">SAMPLE</span></div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {[SkipBack, Pause, SkipForward].map((I, i) => (
            <button key={i} onClick={() => toast.info("Transport controls wire to the Connections Hub Now Playing feed in Phase 6.")}
              className="rounded border border-line p-1.5 text-ink-muted hover:text-ink-primary" data-testid={`prod-transport-btn-${i}`}><I className="h-3.5 w-3.5" /></button>
          ))}
        </div>
        <div className="hidden sm:flex items-center gap-4 ml-auto text-[10px] font-mono text-ink-muted">
          <span>ON AIR <span className="text-brand-amber font-bold">{hhmmss(onAir)}</span></span>
          <span>Listeners <span className="text-ink-primary font-bold">2,347</span></span>
          <span>Stream <span className="text-emerald-400">● Good</span></span>
        </div>
        <div className="hidden lg:flex items-center gap-1.5">
          {providers.slice(0, 4).map((p) => (
            <span key={p.id} className="rounded border border-line px-1.5 py-0.5 font-mono text-[8px] uppercase text-ink-muted">{p.label.split(" ")[0]}</span>
          ))}
        </div>
      </footer>
    </div>
  );
}
